import { FC, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { deleteAvailableCategories } from 'src/assets/static/view_checkbook/useCheckbookCategories';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useYearView from 'src/hooks/useYearView';
import useDeleteGLCodeApi from 'src/services/useDeleteGLCodeApi';
import DeleteGLCodeModal from './DeleteGLCodeModal';

interface DeleteGLCodeProps {
  selectedRows: any[];
}

const DeleteGLCode: FC<DeleteGLCodeProps> = ({ selectedRows }) => {
  const {
    setGlCodePeriodBudget,
    setGlCodesBudgetByYear,
    t,
    setOriginalGlCodePeriodBudget,
    setOriginalGlCodeYearBudget,
    setEditingGlCodePeriodBudget,
    isPeriodView,
    isEditMode,
    setEditingGlCodeYearBudget,
    refreshGLCodePeriodTable,
    setPeriodCheckbookDataState,
    periodCheckbookDataState,
  } = useViewCheckbook();

  const { dropdownPeriodFilter, periodViewTotal } = usePeriodView();

  const { yearViewDropdownFilter } = useYearView();

  const {
    company: { companyId },
  } = useCompany();
  const { confirm } = useConfirm();
  const { deleteGlCode } = useDeleteGLCodeApi(companyId);

  const isDeleteEligibleRecords = useMemo(
    () => selectedRows.every((item) => !item.isDeleted),
    [selectedRows]
  );
  const isDeleteDisable = useMemo(
    () => selectedRows.length === 0 || !isDeleteEligibleRecords,
    [isDeleteEligibleRecords, selectedRows.length]
  );

  const handleDeleteGLCode = useCallback(async () => {
    const isDeleteAccepted = await confirm({
      title: t('Delete Selected GL Code(s)'),
      content: <DeleteGLCodeModal selectedGLCodes={selectedRows} t={t} />,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
    });
    if (isDeleteAccepted) {
      deleteGlCode({
        glCdIds: selectedRows?.filter((el) => !el?.isNewGlCode)?.map((item) => item.id),
      }).then((res) => {
        if (res?.processedIds.length) {
          toast.success('Record(s) deleted successfully');
        }
        const newlyAddedGLCodes = selectedRows
          ?.filter((el) => el?.isNewGlCode)
          ?.map((item) => item.id);
        const glCdIds = [...res.processedIds];

        if (isEditMode) {
          if (isPeriodView) {
            setGlCodePeriodBudget((prev) => {
              const prevItems = [...prev]
                ?.map((el) =>
                  glCdIds.includes(el?.id) ? { ...el, isActive: false, isDeleted: true } : el
                )
                ?.filter((el) => !newlyAddedGLCodes?.includes(el?.id));
              return prevItems;
            });
            setEditingGlCodePeriodBudget((prev) => {
              const prevItems = [...prev]?.filter(
                (el) => ![...newlyAddedGLCodes, ...glCdIds]?.includes(el?.id)
              );
              return prevItems;
            });
            setOriginalGlCodePeriodBudget((prev) => {
              const prevItems = [...prev]?.filter(
                (el) => ![...newlyAddedGLCodes, ...glCdIds]?.includes(el?.id)
              );
              return prevItems;
            });
          } else {
            setGlCodesBudgetByYear((prev) => {
              const prevItems = [...prev]
                ?.map((el) =>
                  glCdIds.includes(el?.id) ? { ...el, isActive: false, isDeleted: true } : el
                )
                ?.filter((el) => !newlyAddedGLCodes?.includes(el?.id));
              return prevItems;
            });
            setEditingGlCodeYearBudget((prev) => {
              const prevItems = [...prev]?.filter(
                (el) => ![...newlyAddedGLCodes, ...glCdIds]?.includes(el?.id)
              );
              return prevItems;
            });
            setOriginalGlCodeYearBudget((prev) => {
              const prevItems = [...prev]?.filter(
                (el) => ![...newlyAddedGLCodes, ...glCdIds]?.includes(el?.id)
              );
              return prevItems;
            });
          }
        } else {
          refreshGLCodePeriodTable();
        }
      });
      if (
        periodCheckbookDataState.skip &&
        periodCheckbookDataState.take &&
        selectedRows.length === periodViewTotal - periodCheckbookDataState.skip
      ) {
        setPeriodCheckbookDataState({
          ...periodCheckbookDataState,
          skip: periodCheckbookDataState.skip - periodCheckbookDataState.take,
        });
      }
    }
  }, [
    confirm,
    deleteGlCode,
    isEditMode,
    isPeriodView,
    periodCheckbookDataState,
    periodViewTotal,
    refreshGLCodePeriodTable,
    selectedRows,
    setEditingGlCodePeriodBudget,
    setEditingGlCodeYearBudget,
    setGlCodePeriodBudget,
    setGlCodesBudgetByYear,
    setOriginalGlCodePeriodBudget,
    setOriginalGlCodeYearBudget,
    setPeriodCheckbookDataState,
    t,
  ]);

  return (
    <>
      {deleteAvailableCategories.includes(
        isPeriodView ? dropdownPeriodFilter.value : yearViewDropdownFilter.value
      ) && (
        <PopUp
          popupAlign={{ horizontal: 'center', vertical: 'top' }}
          content={
            <div className="m-1 max-w-[350px]">
              <PopUpContentWrapper
                type={selectedRows.length > 0 ? 'Error' : 'Information'}
                contents={
                  selectedRows.length > 0
                    ? ['Selected record(s) must be in Undeleted state']
                    : ['Please select record(s) to Delete']
                }
              />
            </div>
          }
          show={isDeleteDisable}
        >
          <Button onClick={handleDeleteGLCode} disabled={isDeleteDisable} color="error">
            Delete
          </Button>
        </PopUp>
      )}
    </>
  );
};

export default DeleteGLCode;
